import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Edit2, Trash2, Copy, Plus, X, Tag, Check } from "lucide-react";
import Axios from 'axios';

function Names(props) {
  const [items, setItems] = useState([]);
  const [loginStatus, setLoginStatus] = useState(false);
  const [activeCategory, setActiveCategory] = useState('All');
  const [newCategory, setNewCategory] = useState('');
  const [showCategoryInput, setShowCategoryInput] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [customCategories, setCustomCategories] = useState([]);
  const [sortBy, setSortBy] = useState('date');
  const [copiedId, setCopiedId] = useState(null);
  const categoryInputRef = useRef(null);

  Axios.defaults.withCredentials = true;

  useEffect(() => {
    const fetchProtected = async () => {
      try {
        const response = await Axios.get('https://mygamename.com/php/protected.php', { withCredentials: true });
        if (response.data.status === 'success') {
          props.setUEmail(response.data.email);
          setLoginStatus(true);
          Axios.post("https://mygamename.com/php/names.php", {
            email: response.data.email,
          }).then((response) => {
            if (Array.isArray(response.data)) setItems(response.data);
          });
        }
      } catch (error) {
        console.error(error);
      }
    };
    fetchProtected();
  }, []);

  useEffect(() => {
    if (showCategoryInput && categoryInputRef.current) categoryInputRef.current.focus();
  }, [showCategoryInput]);

  const categoriesFromItems = Array.from(
    new Set(items.map(i => i.category).filter(c => c && c !== 'Uncategorized'))
  );
  const categories = ['All', 'Uncategorized', ...Array.from(
    new Set([...categoriesFromItems, ...customCategories])
  )];

  const filteredItems = (activeCategory === 'All'
    ? items
    : items.filter(i => (i.category || 'Uncategorized') === activeCategory)
  ).slice().sort((a, b) =>
    sortBy === 'alpha' ? a.name.localeCompare(b.name) : 0
  );

  const handleCopy = (id, name) => {
    navigator.clipboard.writeText(name);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this name?")) return;
    setItems(items.filter(item => item.id !== id));
    Axios.post("https://mygamename.com/php/delete.php", { id });
  };

  const handleCategoryChange = (id, category) => {
    setItems(items.map(item => item.id === id ? { ...item, category } : item));
    setEditingCategoryId(null);
    Axios.post("https://mygamename.com/php/update_category.php", { id, category });
  };

  const handleAddCategory = () => {
    const trimmed = newCategory.trim();
    if (!trimmed) { setShowCategoryInput(false); setNewCategory(''); return; }
    if (!categories.includes(trimmed)) setCustomCategories(prev => [...prev, trimmed]);
    setActiveCategory(trimmed);
    setShowCategoryInput(false);
    setNewCategory('');
  };

  return (
    <div className="mx-auto max-w-2xl py-12 px-4 flex flex-col gap-6">

      {!loginStatus ? (
        <div className="bg-[#1a1a1a] rounded-xl border border-[#222222] p-12 text-center">
          <p className="text-[#F5F5F5]/50 text-base mb-4">Sign in to view your saved names.</p>
          <div className="flex items-center justify-center gap-3">
            <Link to='/login' className="px-4 py-2 rounded-lg bg-[#8B5CF6] hover:bg-[#7C3AED] text-[#F5F5F5] text-sm font-semibold transition-colors">Login</Link>
            <Link to='/signup' className="px-4 py-2 rounded-lg border border-[#333333] text-[#F5F5F5]/60 text-sm font-semibold hover:border-[#8B5CF6] hover:text-[#F5F5F5] transition-colors">Sign up</Link>
          </div>
        </div>
      ) : (
        <>
          {/* Category filter row */}
          <div className="flex items-center gap-2 flex-wrap">
            {categories.map(cat => {
              const count = cat === 'All' ? items.length : items.filter(i => (i.category || 'Uncategorized') === cat).length;
              return (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border ${
                    activeCategory === cat
                      ? 'bg-[#8B5CF6] border-[#8B5CF6] text-[#F5F5F5]'
                      : 'bg-transparent border-[#333333] text-[#F5F5F5]/50 hover:border-[#8B5CF6] hover:text-[#F5F5F5]'
                  }`}
                >
                  {cat}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                    activeCategory === cat ? 'bg-white/20 text-white' : 'bg-[#222222] text-[#F5F5F5]/40'
                  }`}>{count}</span>
                </button>
              );
            })}

            {showCategoryInput ? (
              <div className="flex items-center gap-1">
                <input
                  ref={categoryInputRef}
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAddCategory(); if (e.key === 'Escape') { setShowCategoryInput(false); setNewCategory(''); } }}
                  placeholder="Category name"
                  className="bg-[#111111] border border-[#8B5CF6] text-[#F5F5F5] text-xs rounded-full px-3 py-1.5 outline-none w-32"
                />
                <button onClick={handleAddCategory} className="text-[#8B5CF6] hover:text-[#F5F5F5] transition-colors"><Plus size={14} /></button>
                <button onClick={() => { setShowCategoryInput(false); setNewCategory(''); }} className="text-[#F5F5F5]/30 hover:text-[#F5F5F5] transition-colors"><X size={14} /></button>
              </div>
            ) : (
              <button
                onClick={() => setShowCategoryInput(true)}
                className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs border border-dashed border-[#333333] text-[#F5F5F5]/30 hover:border-[#8B5CF6] hover:text-[#8B5CF6] transition-colors"
              >
                <Plus size={11} /> New
              </button>
            )}
          </div>

          {/* List card */}
          <div className="bg-[#1a1a1a] rounded-xl border border-[#222222] overflow-hidden">

            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#222222]">
              <div>
                <h1 className="text-base font-bold text-[#F5F5F5]">
                  {activeCategory === 'All' ? 'All Names' : activeCategory}
                </h1>
                <p className="text-[#F5F5F5]/30 text-xs mt-0.5">{filteredItems.length} {filteredItems.length === 1 ? 'name' : 'names'}</p>
              </div>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-[#111111] border border-[#333333] text-[#F5F5F5]/60 text-xs rounded-lg px-2 py-1.5 outline-none focus:border-[#8B5CF6] transition-colors cursor-pointer"
              >
                <option value="date">Date Created</option>
                <option value="alpha">Alphabetical</option>
              </select>
            </div>

            {/* Rows */}
            {filteredItems.length === 0 ? (
              <p className="text-[#F5F5F5]/30 text-sm py-12 text-center">No names in this category.</p>
            ) : (
              <ul className="divide-y divide-[#1e1e1e]">
                {filteredItems.map((item) => (
                  <li key={item.id} className="px-5 py-4 hover:bg-[#1e1e1e] transition-colors group">
                    <div className="flex items-center justify-between gap-3">

                      {/* Name + meta */}
                      <div className="flex-1 min-w-0">
                        <p className="text-[#F5F5F5] font-mono text-base truncate">{item.name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[#F5F5F5]/25 text-xs">{item.created_at || '—'}</span>
                          {editingCategoryId === item.id ? (
                            <select
                              autoFocus
                              defaultValue={item.category || 'Uncategorized'}
                              onBlur={(e) => handleCategoryChange(item.id, e.target.value)}
                              onChange={(e) => handleCategoryChange(item.id, e.target.value)}
                              className="bg-[#111111] border border-[#8B5CF6] text-[#F5F5F5] text-xs rounded px-1.5 py-0.5 outline-none"
                            >
                              {categories.filter(c => c !== 'All').map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                              ))}
                              {!categories.includes(item.category) && item.category && (
                                <option value={item.category}>{item.category}</option>
                              )}
                            </select>
                          ) : (
                            <button
                              onClick={() => setEditingCategoryId(item.id)}
                              className="flex items-center gap-1 text-[#F5F5F5]/25 text-xs hover:text-[#8B5CF6] transition-colors"
                            >
                              <Tag size={10} />
                              {item.category || 'Uncategorized'}
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Actions — visible on hover */}
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => handleCopy(item.id, item.name)}
                          className={`p-2 rounded-md border transition-colors ${
                            copiedId === item.id
                              ? 'border-[#8B5CF6] text-[#8B5CF6] bg-[#8B5CF6]/10'
                              : 'border-[#2a2a2a] text-[#F5F5F5]/50 hover:border-[#8B5CF6] hover:text-[#F5F5F5]'
                          }`}
                          aria-label={`Copy ${item.name}`}
                        >
                          {copiedId === item.id ? <Check size={14} /> : <Copy size={14} />}
                        </button>
                        <Link to={`/names/${item.id}`}>
                          <button
                            className="p-2 rounded-md border border-[#2a2a2a] text-[#F5F5F5]/50 hover:border-[#8B5CF6] hover:text-[#F5F5F5] transition-colors"
                            aria-label={`Edit ${item.name}`}
                          >
                            <Edit2 size={14} />
                          </button>
                        </Link>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="p-2 rounded-md border border-[#2a2a2a] text-[#F5F5F5]/50 hover:border-red-500 hover:text-red-400 transition-colors"
                          aria-label={`Delete ${item.name}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default Names;
